import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import EmailIcon from '@mui/icons-material/Email';
import { useManagementAuth } from '../../auth/ManagementAuthContext';
import api from '../../services/api';
import { EmailSetting, EmailSettingUpdate } from '../../types';

const EmailSettingsPage: React.FC = () => {
  const { managementToken } = useManagementAuth();
  const [settings, setSettings] = useState<EmailSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      if (!managementToken) {
        setLoading(false);
        return;
      }
      try {
        const data = await api.managementAuth.getEmailSettings(managementToken);
        setSettings(data);
      } catch (error) {
        console.error('メール受信設定の取得に失敗しました', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [managementToken]);

  const handleToggle = (storeId: string) => {
    setSettings(prev =>
      prev.map(s =>
        s.store_id === storeId ? { ...s, is_enabled: !s.is_enabled } : s
      )
    );
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!managementToken) return;
    setSaving(true);
    setMessage(null);
    try {
      const updates: EmailSettingUpdate[] = settings.map(s => ({
        store_id: s.store_id,
        is_enabled: s.is_enabled,
      }));
      const updated = await api.managementAuth.updateEmailSettings(managementToken, updates);
      setSettings(updated);
      setHasChanges(false);
      setMessage({ type: 'success', text: 'メール受信設定を更新しました' });
    } catch {
      setMessage({ type: 'error', text: '更新に失敗しました' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" color="text.primary" sx={{ mb: 1 }}>
        メール受信設定
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        現在のアカウントでメールを受け取る店舗を設定できます。
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Card>
        <CardHeader
          avatar={<EmailIcon color="primary" />}
          title="店舗別メール受信設定"
          action={
            hasChanges ? (
              <Button
                variant="contained"
                size="small"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? <CircularProgress size={20} /> : '保存'}
              </Button>
            ) : null
          }
        />
        <CardContent sx={{ p: 0 }}>
          <List disablePadding>
            {settings.map((setting, index) => (
              <ListItem
                key={setting.store_id}
                divider={index < settings.length - 1}
                secondaryAction={
                  <Switch
                    edge="end"
                    checked={setting.is_enabled}
                    onChange={() => handleToggle(setting.store_id)}
                  />
                }
              >
                <ListItemText
                  primary={setting.store_name || '（店舗名未設定）'}
                  secondary={setting.is_enabled ? 'メールを受信する' : 'メールを受信しない'}
                />
              </ListItem>
            ))}
            {settings.length === 0 && (
              <ListItem>
                <ListItemText
                  primary="設定可能な店舗がありません"
                  sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}
                />
              </ListItem>
            )}
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EmailSettingsPage;
